import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { XCircle } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/Dialog";
import { toast } from "@/components/ui/Toast";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// RejectDialog
//
// Modal to reject an IN_REVIEW assessment with rationale.
// Design: §6.2.2 — Reject action with rationale
// ─────────────────────────────────────────────

interface RejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentId: string;
  onSuccess?: () => void;
}

export function RejectDialog({
  open,
  onOpenChange,
  assessmentId,
  onSuccess,
}: RejectDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [rationale, setRationale] = useState("");

  const mutation = useMutation({
    mutationFn: (body: { comment: string }) =>
      api.post(`/assessments/${assessmentId}/reject`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", assessmentId] });
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
      toast.success(t("assessment.rejectDialog.rejectedSuccess"));
      onOpenChange(false);
      setRationale("");
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("assessment.rejectDialog.rejectFailed"));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!rationale.trim()) return;
    mutation.mutate({ comment: rationale.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full"
              style={{ backgroundColor: "var(--danger-bg)" }}
            >
              <XCircle className="h-5 w-5" style={{ color: "var(--danger)" }} />
            </div>
            <DialogTitle>{t("assessment.rejectDialog.title")}</DialogTitle>
          </div>
          <DialogDescription>
            {t("assessment.rejectDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("assessment.rejectDialog.rationaleLabel")} <span className="text-danger">*</span>
            </label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder={t("assessment.rejectDialog.rationalePlaceholder")}
              rows={4}
              required
              className="min-h-[100px] w-full rounded-control border border-border-default bg-bg-input px-3 py-2 text-[14px] text-text-primary placeholder:text-text-muted resize-y focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onOpenChange(false);
                setRationale("");
              }}
              disabled={mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              isLoading={mutation.isPending}
              disabled={!rationale.trim()}
            >
              {t("assessment.rejectDialog.title")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
